import { v4 } from 'uuid';
import { DG } from '../dg';
import { coco, type ResourceOutput } from '@cotera/api';
import { Folder } from './folder';
import { Lazy } from './lazy';
import { fromIsoString, toIsoString } from './iso-date';
import {
  DEFAULT_DOCUMENT_STATE,
  parseDocumentState,
  titleFromDocumentState,
} from './parse-document-state';
import { WorkspacePromptEditor } from './workspace-prompt-editor';
import type { WorkspaceDomainLoader } from './workspace-prefetch';
import { Erod, Ty } from '@cotera/era';

export type AgentResourceOutput = Extract<ResourceOutput, { type: 'agent' }>;

/**
 * Data shape for an Agent (without hydrated EditorState).
 * Use {@link Agent.fromData} with {@link AgentHydration} (`loadFolder`, `workspaceDomainLoader`) to create an Agent.
 */
export type AgentData = {
  id: string;
  orgId: string;
  /** User ID that created/owns the agent. */
  ownerId: string;
  editorState: DG.DocumentEditor.DocumentState;
  model: string;
  description: string | null;
  agentKey: string;
  /** User ID that has the agent locked, or null when unlocked */
  lockedBy?: string | null;
  /** Resource status (active/archived). Defaults to active when no resources row exists. */
  status?: 'active' | 'archived';
  createdAt: Date;
  updatedAt: Date;
};

export type SerializedAgent = {
  id: string;
  orgId: string;
  ownerId: string;
  editorState: DG.DocumentEditor.DocumentState;
  model: string;
  description: string | null;
  agentKey: string;
  lockedBy?: string | null;
  status?: 'active' | 'archived';
  createdAt: string;
  updatedAt: string;
};

export type AgentHydration = {
  readonly loadFolder: () => Promise<Folder | null>;
  readonly preloadedFolder?: Folder | null;
  /** Resolves @mentions when compiling this agent’s workspace prompt. */
  readonly workspaceDomainLoader: WorkspaceDomainLoader;
};

/**
 * Write model for creating agents.
 */
export type CreateAgentData = {
  readonly editorState: DG.DocumentEditor.DocumentState;
  readonly model: string;
  /** Optional UUID to use for the new agent. When omitted, one is generated. */
  readonly id?: string;
  /** User ID that created/owns the agent. Required when creating. */
  readonly ownerId: string;
  /** User ID that has the agent locked, or null when unlocked. Used when updating. */
  readonly lockedBy?: string | null;
  /**
   * The conversation this agent was created in. Set only when a chat creates
   * it; a provenance breadcrumb the Library groups by. Omitted otherwise.
   */
  readonly sourceConversationId?: string | null;
};

/**
 * Domain model for an Agent.
 * Exposes {@link WorkspacePromptEditor} for prompt compilation (no data graph).
 * Lives alongside Conversation in cotera-sdk.
 */
export class Agent {
  private readonly _data: AgentData;
  private readonly _workspacePromptEditor: WorkspacePromptEditor;
  readonly folder: Lazy<Folder | null>;

  private constructor(data: AgentData, hydration: AgentHydration) {
    this._data = { ...data };
    this._workspacePromptEditor = new WorkspacePromptEditor(
      data.editorState,
      hydration.workspaceDomainLoader
    );
    this.folder = new Lazy(hydration.loadFolder, hydration.preloadedFolder);
  }

  /**
   * Create data for a new, empty agent.
   */
  static createNew(orgId: string, ownerId: string, id?: string): AgentData {
    const agentId = id ?? v4();
    const now = new Date();
    return {
      id: agentId,
      orgId,
      ownerId,
      editorState: DEFAULT_DOCUMENT_STATE,
      model: coco.DEFAULT_AGENT_MODEL,
      description: null,
      agentKey: `agent-${agentId.replace(/-/g, '')}`,
      createdAt: now,
      updatedAt: now,
    };
  }

  static fromData(
    data: Omit<AgentData, 'editorState'> & {
      editorState?: unknown;
      description?: string | null;
    },
    hydration: AgentHydration
  ): Agent {
    const editorState = parseDocumentState(data.editorState);
    return new Agent(
      {
        ...data,
        editorState,
        description: data.description ?? null,
        agentKey: data.agentKey,
        status: data.status ?? 'active',
      },
      hydration
    );
  }

  static fromSerialized(
    serialized: SerializedAgent,
    hydration: AgentHydration
  ): Agent {
    return Agent.fromData(
      {
        id: serialized.id,
        orgId: serialized.orgId,
        ownerId: serialized.ownerId,
        editorState: serialized.editorState,
        model: serialized.model,
        description: serialized.description,
        agentKey: serialized.agentKey,
        lockedBy: serialized.lockedBy,
        status: serialized.status,
        createdAt: fromIsoString(serialized.createdAt),
        updatedAt: fromIsoString(serialized.updatedAt),
      },
      hydration
    );
  }

  static fromResourceOutput(
    output: AgentResourceOutput,
    workspaceDomainLoader: WorkspaceDomainLoader
  ): Agent {
    const a = output.agent;
    const folderId = output.meta?.folderId ?? null;
    return Agent.fromData(
      {
        ownerId: a.ownerId,
        id: a.id,
        orgId: a.orgId,
        editorState: a.editorState,
        model: a.model,
        description: a.description ?? null,
        agentKey: a.agentKey ?? `agent-${a.id.replace(/-/g, '')}`,
        lockedBy: a.locked ? 'system' : null,
        status: a.status,
        createdAt: a.createdAt,
        updatedAt: a.updatedAt,
      },
      {
        loadFolder: async () =>
          folderId !== null ? workspaceDomainLoader.loadFolder(folderId) : null,
        workspaceDomainLoader,
      }
    );
  }

  get ownerId(): string {
    return this._data.ownerId;
  }

  get id(): string {
    return this._data.id;
  }

  get lockedBy(): string | null {
    return this._data.lockedBy ?? null;
  }

  get orgId(): string {
    return this._data.orgId;
  }

  get inputSchema(): Erod.Def<unknown> {
    const inputTy = this.editorState.input();
    const isPlainStringPrompt =
      inputTy.ty.k === 'primitive' && inputTy.ty.t === 'string';

    if (isPlainStringPrompt) {
      return Erod.Schema.object({
        prompt: Erod.Schema.string(),
      }) as Erod.Def<unknown>;
    }

    if (inputTy.ty.k !== 'struct') {
      return Erod.Schema.object({
        prompt: Erod.Schema.string(),
      }) as Erod.Def<unknown>;
    }

    const descriptions = new Map(
      this.editorState.inputFields().map((f) => [f.name, f.description])
    );
    const dynamicFields: Record<string, Erod.Def<unknown>> = {};
    for (const [key, fieldTy] of Object.entries(inputTy.ty.fields)) {
      dynamicFields[key] = Erod.Schema.bareSchemaFromTy(fieldTy).describe(
        descriptions.get(key) ??
          `Value for input "${key}" declared in the agent's instructions`
      );
    }

    return Erod.Schema.object(dynamicFields) as Erod.Def<unknown>;
  }

  get inputTy(): Ty.ExtendedAttributeType {
    return this.editorState.input();
  }

  get inputTyString(): string {
    return Ty.displayTy(this.inputTy);
  }

  /**
   * Workspace-oriented prompt editor for this agent’s TipTap document.
   */
  get editorState(): WorkspacePromptEditor {
    return this._workspacePromptEditor;
  }

  get model(): string {
    return this._data.model;
  }

  get createdAt(): Date {
    return this._data.createdAt;
  }

  get updatedAt(): Date {
    return this._data.updatedAt;
  }

  get description(): string | null {
    return this._data.description ?? null;
  }

  get agentKey(): string {
    return this._data.agentKey;
  }

  get locked(): boolean {
    return this._data.lockedBy != null;
  }

  /** Resource status (active/archived). */
  get status(): 'active' | 'archived' {
    return this._data.status ?? 'active';
  }

  /**
   * First H1 from editor JSON, or null (same rule as graph `EditorState.title()`).
   */
  title(): string | null {
    return titleFromDocumentState(this._data.editorState);
  }

  /** Same instance as {@link Agent#editorState}. */
  getEditorState(): WorkspacePromptEditor {
    return this._workspacePromptEditor;
  }

  get outputShape(): Ty.ExtendedAttributeType {
    return this.editorState.getOutputTy();
  }

  get outputTyString(): string {
    return Ty.displayTy(this.outputShape);
  }

  get agentData(): AgentData {
    return { ...this._data };
  }

  serialize(): SerializedAgent {
    return {
      id: this._data.id,
      orgId: this._data.orgId,
      ownerId: this._data.ownerId,
      editorState: this._data.editorState,
      model: this._data.model,
      description: this._data.description,
      agentKey: this._data.agentKey,
      lockedBy: this._data.lockedBy,
      status: this._data.status,
      createdAt: toIsoString(this._data.createdAt),
      updatedAt: toIsoString(this._data.updatedAt),
    };
  }

  /**
   * Structural equality for agent data. Used to avoid redundant updates when
   * broadcast events deliver the same state (e.g. after a local save).
   */
  isEqual(other: Agent): boolean {
    if (this._data.id !== other._data.id) return false;
    if (this._data.model !== other._data.model) return false;
    if (this.locked !== other.locked) return false;
    if (this.status !== other.status) return false;
    if (this._data.updatedAt.getTime() !== other._data.updatedAt.getTime())
      return false;
    if (
      JSON.stringify(this._data.editorState) !==
      JSON.stringify(other._data.editorState)
    )
      return false;
    return true;
  }
}
